import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { FaGraduationCap, FaCheckCircle, FaChevronDown, FaPlane, FaBriefcase, FaFileAlt, FaHandshake, FaSearch } from "react-icons/fa";
import "../Styles/StudyVisa.css";
import Reveal from "./Reveal";
import MagneticButton from "./MagneticButton";

const services = [
  { icon: <FaGraduationCap />, title: "University Selection", desc: "Personalized shortlisting based on your profile, budget, and career goals" },
  { icon: <FaFileAlt />, title: "SOP & LOR Writing", desc: "Compelling statements that get you admitted and approved" },
  { icon: <FaBriefcase />, title: "Scholarship Guidance", desc: "Identify and apply for scholarships to reduce your financial burden" },
  { icon: <FaPlane />, title: "Visa Filing", desc: "Complete documentation, application, and interview preparation" },
  { icon: <FaCheckCircle />, title: "Admission Support", desc: "Track applications and negotiate offers with universities" },
  { icon: <FaHandshake />, title: "Pre-Departure Help", desc: "Accommodation, forex, travel, and orientation support" },
];

const countries = [
  {
    flag: "🇨🇦", name: "Canada",
    tuition: "CAD 15,000 – 35,000/year",
    workDuring: "24 hrs/week during semester, full-time on breaks",
    postStudy: "PGWP up to 3 years",
    pr: "Canadian Experience Class (CEC)",
    ielts: "6.5 overall",
    best: ["Engineering", "Business", "Computer Science", "Nursing"],
    highlights: ["Clear PR pathway after graduation", "Multicultural & welcoming environment", "Affordable compared to US & UK", "Top-ranked universities (UofT, UBC, McGill)"],
    docs: ["Acceptance letter from a DLI", "Bank statements (CAD 10,000+ beyond tuition)", "GIC of CAD 10,000", "Statement of Purpose (SOP)", "Valid passport", "IELTS/TOEFL scores"],
  },
  {
    flag: "🇺🇸", name: "United States",
    tuition: "USD 25,000 – 60,000/year",
    workDuring: "20 hrs/week on campus only",
    postStudy: "OPT 1 year (STEM: 3 years)",
    pr: "H1B Visa → Green Card",
    ielts: "6.5 – 7.5 (varies by university)",
    best: ["STEM", "Business", "Law", "Medicine"],
    highlights: ["World's top-ranked universities (MIT, Harvard, Stanford)", "STEM OPT extension up to 3 years", "Massive research & innovation ecosystem", "Diverse programs and campuses"],
    docs: ["Form I-20 from US university", "DS-160 visa application form", "SEVIS fee payment receipt", "Financial proof (USD 25,000+/year)", "Valid passport", "IELTS/TOEFL/GRE/GMAT scores"],
  },
  {
    flag: "🇦🇺", name: "Australia",
    tuition: "AUD 20,000 – 45,000/year",
    workDuring: "48 hrs/fortnight",
    postStudy: "Graduate Visa (485) 2–4 years",
    pr: "Skilled Independent Visa (189/190)",
    ielts: "6.0 – 6.5 overall",
    best: ["Nursing", "Engineering", "Accounting", "IT"],
    highlights: ["Post-study work visa up to 4 years", "Clear skilled migration PR pathway", "High quality of life & safety", "Group of Eight universities (ANU, Melbourne, Sydney)"],
    docs: ["Confirmation of Enrolment (CoE)", "Genuine Temporary Entrant (GTE) statement", "Bank statements (AUD 21,041+)", "Overseas Student Health Cover (OSHC)", "Valid passport", "IELTS/PTE scores"],
  },
  {
    flag: "🇬🇧", name: "United Kingdom",
    tuition: "£12,000 – 30,000/year",
    workDuring: "20 hrs/week during term, full-time during vacations",
    postStudy: "Graduate Route (PSW) 2 years (PhD: 3 years)",
    pr: "Skilled Worker Visa → ILR (5 years)",
    ielts: "6.0 – 6.5 overall",
    best: ["Finance", "Business", "Data Science", "Medicine"],
    highlights: ["World-renowned universities (Oxford, Cambridge, Imperial)", "Fast-track degrees (1-year Master's, 3-year Bachelor's)", "Post-Study Graduate Route (2-year PSW)", "Strong financial & tech hub access"],
    docs: ["Confirmation of Acceptance for Studies (CAS)", "Tuberculosis (TB) test certificate", "Financial proof (£1,334/mo London, £1,023/mo outside)", "Statement of Purpose (SOP)", "Valid passport", "IELTS/TOEFL scores"],
  },
  {
    flag: "🇩🇪", name: "Germany",
    tuition: "Free at Public Universities (semester fee €150-350)",
    workDuring: "140 full days or 280 half days per year",
    postStudy: "18-month Job Seeker Visa after graduation",
    pr: "EU Blue Card → Permanent Residence (21-36 months)",
    ielts: "6.0 – 6.5 (English programs) or German B2/C1",
    best: ["Automotive Engineering", "Mechanical Engineering", "IT", "Renewable Energy"],
    highlights: ["Tuition-free higher education at public universities", "Fast-track PR and citizenship (3-5 years) under new laws", "Robust industrial economy and high job availability", "Excellent research facilities and engineering schools"],
    docs: ["Admission letter (Zulassungsbescheid)", "Blocked bank account (€11,904/year)", "Health insurance coverage", "Statement of Purpose (SOP)", "Valid passport", "IELTS or German language certificate"],
  },
  {
    flag: "🇳🇿", name: "New Zealand",
    tuition: "NZD 22,000 – 40,000/year",
    workDuring: "20 hrs/week during term, full-time during holidays",
    postStudy: "Post-Study Work Visa (PSWV) up to 3 years",
    pr: "Skilled Migrant Category or Green List",
    ielts: "6.0 – 6.5 overall",
    best: ["Information Technology", "Agriculture", "Tourism & Hospitality", "Nursing"],
    highlights: ["Post-study work rights up to 3 years", "High safety, clean environment, & incredible work-life balance", "Straight-to-Residence pathways for Green List occupations", "Highly respected education system based on British model"],
    docs: ["Offer of Place from NZ university", "Financial proof (NZD 20,000/year)", "Medical and chest X-ray certificate", "Police clearance certificate", "Valid passport", "IELTS/PTE scores"],
  },
];

const steps = [
  { num: 1, title: "Free Profile Assessment", desc: "We evaluate your academic background, budget, and career goals" },
  { num: 2, title: "University Shortlisting", desc: "We match you with the best universities for your profile" },
  { num: 3, title: "Application & SOP", desc: "We prepare your application, SOP, and LORs" },
  { num: 4, title: "Admission & Scholarship", desc: "We secure your offer letter and explore scholarship options" },
  { num: 5, title: "Visa Documentation", desc: "We prepare and file your complete student visa application" },
  { num: 6, title: "Pre-Departure Support", desc: "Accommodation, travel, and orientation guidance" },
];

const faqs = [
  { q: "Which country is best for Indian students — Canada, US, or Australia?", a: "Canada is best for those seeking a clear PR pathway after graduation. The US is ideal for STEM students wanting top-ranked universities and OPT/STEM OPT. Australia offers the longest post-study work visa (up to 4 years) and a strong skilled migration pathway." },
  { q: "What IELTS score do I need for a student visa?", a: "Canada requires 6.5 overall (no band below 6.0). The US varies by university but typically 6.5–7.5. Australia requires 6.0–6.5 overall. Some universities accept PTE or TOEFL as alternatives." },
  { q: "Can I work while studying in Canada, US, or Australia?", a: "Canada allows 24 hrs/week during semesters and full-time during breaks. The US only allows 20 hrs/week on-campus work on an F-1 visa. Australia allows 48 hours per fortnight (about 24 hrs/week)." },
  { q: "What is a GIC and is it mandatory for Canada?", a: "A Guaranteed Investment Certificate (GIC) is a CAD 10,000 deposit with a Canadian bank that proves you can support yourself. It is strongly recommended (and required by most visa officers) for Canadian student visa applications." },
  { q: "What is STEM OPT in the US?", a: "Optional Practical Training (OPT) allows F-1 students to work in the US for 1 year after graduation. If your degree is in a STEM field, you can extend this by 2 more years (total 3 years) under the STEM OPT extension." },
  { q: "How long does a student visa take to process?", a: "Canada: 4–8 weeks. US (F-1 visa): 3–5 weeks after I-20 issuance. Australia: 4–6 weeks. Apply at least 3–4 months before your program start date." },
];

const destinationCountries = [
  { name: "Canada", value: "Canada" },
  { name: "United States", value: "United States" },
  { name: "Australia", value: "Australia" },
  { name: "United Kingdom", value: "United Kingdom" },
  { name: "Germany", value: "Germany" },
  { name: "Ireland", value: "Ireland" },
  { name: "Netherlands", value: "Netherlands" },
  { name: "France", value: "France" },
  { name: "Sweden", value: "Sweden" },
  { name: "Denmark", value: "Denmark" },
  { name: "Norway", value: "Norway" },
  { name: "Finland", value: "Finland" },
  { name: "Switzerland", value: "Switzerland" },
  { name: "Austria", value: "Austria" },
  { name: "Belgium", value: "Belgium" },
  { name: "New Zealand", value: "New Zealand" },
  { name: "Singapore", value: "Singapore" },
  { name: "Malaysia", value: "Malaysia" },
  { name: "Japan", value: "Japan" },
  { name: "South Korea", value: "South Korea" },
  { name: "Dubai / UAE", value: "Dubai / UAE" },
  { name: "Not Sure Yet", value: "Not Sure" },
];

function SearchableFormDropdown({ value, onChange, required }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
    }
  }, [isOpen]);

  const filtered = destinationCountries.filter(c =>
    c.name.toLowerCase().startsWith(searchQuery.toLowerCase())
  );

  const selectedDisplay = destinationCountries.find(c => c.value === value)?.name || "";

  return (
    <div className="studyvisa-form-dropdown" ref={dropdownRef}>
      <div 
        className="studyvisa-form-dropdown-trigger d-flex align-items-center justify-content-between"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="dropdown-trigger-text">
          {selectedDisplay || "Select Destination"}
        </span>
        <FaChevronDown className={`dropdown-chevron ${isOpen ? "open" : ""}`} />
      </div>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="studyvisa-form-dropdown-menu position-absolute shadow-lg"
          >
            <div className="dropdown-search-box p-2 d-flex align-items-center gap-2 border-bottom">
              <FaSearch className="dropdown-search-icon text-muted ms-2" />
              <input
                type="text"
                className="dropdown-search-input w-100 border-0 bg-transparent py-1 ps-1 pe-2 shadow-none"
                placeholder="Search destination..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="dropdown-search-clear border-0 bg-transparent text-muted"
                >
                  &times;
                </button>
              )}
            </div>
            <div className="dropdown-items-list">
              {filtered.map((c, i) => (
                <button
                  key={i}
                  type="button"
                  className={`studyvisa-form-dropdown-item d-flex align-items-center w-100 ${value === c.value ? "active" : ""}`}
                  onClick={() => {
                    onChange(c.value);
                    setIsOpen(false);
                  }}
                >
                  {c.name}
                  {value === c.value && <span className="ms-auto selected-dot"></span>}
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="dropdown-no-results p-3 text-center text-muted">
                  No destinations starting with "{searchQuery}"
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {required && (
        <input
          type="text"
          value={value}
          required
          onChange={() => {}}
          style={{
            position: 'absolute',
            opacity: 0,
            pointerEvents: 'none',
            height: 0,
            width: 0,
            bottom: 0
          }}
        />
      )}
    </div>
  );
}

export default function StudyVisa() {
  const [activeCountry, setActiveCountry] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownSearchQuery, setDropdownSearchQuery] = useState("");
  const [openFaq, setOpenFaq] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", destination: "", course: "" });
  const [submitted, setSubmitted] = useState(false);
  const country = countries[activeCountry];

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isDropdownOpen) {
      setDropdownSearchQuery("");
    }
  }, [isDropdownOpen]);

  const filteredCountries = countries
    .map((c, originalIndex) => ({ ...c, originalIndex }))
    .filter((c) =>
      c.name.toLowerCase().startsWith(dropdownSearchQuery.toLowerCase())
    );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await fetch(`${process.env.REACT_APP_API_URL || "https://veabroad.com"}/api/studyVisaForm/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, category: "Study Visa", source: "Study visa page" }),
      });
    } catch {}
    setSubmitted(true);
  };

  return (
    <div className="studyvisa-page">
      <Helmet>
        <title>Student Visa Guidance | Canada Study Permit, US F-1 & Aus 500</title>
        <meta name="description" content="Secure your student visa for Canada, USA, and Australia. Complete support with documentation, GIC, health cover, and SOP preparation. Achieve 95%+ success!" />
        <link rel="canonical" href="https://veabroad.com/study-visa" />
        <meta property="og:title" content="Student Visa Guidance | Canada Study Permit, US F-1 & Aus 500" />
        <meta property="og:description" content="Secure your student visa for Canada, USA, and Australia. Complete support with documentation, GIC, health cover, and SOP preparation. Achieve 95%+ success!" />
        <meta property="og:url" content="https://veabroad.com/study-visa" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://veabroad.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Student Visa Guidance | Canada Study Permit, US F-1 & Aus 500" />
        <meta name="twitter:description" content="Secure your student visa for Canada, USA, and Australia. Complete support with documentation, GIC, health cover, and SOP preparation. Achieve 95%+ success!" />
        <meta name="twitter:image" content="https://veabroad.com/og-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Student Visa Assistance",
            "provider": {
              "@type": "EducationalOrganization",
              "name": "Vidya Education Abroad",
              "url": "https://veabroad.com/"
            },
            "areaServed": "IN",
            "description": "Professional support for preparing, filing, and securing study permits and student visas globally."
          })}
        </script>
      </Helmet>

      {/* HERO */}
      <section className="studyvisa-hero position-relative" style={{ overflow: "hidden" }}>
        <div className="studyvisa-hero-overlay" />

        {/* Animated Background Blobs */}
        <div className="va-blob-field" aria-hidden="true">
          <div className="va-blob va-blob-mint" style={{ top: '0%', left: '5%', width: '320px', height: '320px' }} />
          <div className="va-blob va-blob-teal" style={{ bottom: '-10%', right: '10%', width: '380px', height: '380px' }} />
        </div>

        <div className="containerStudy studyvisa-hero-inner">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="studyvisa-hero-text"
          >
            <div className="studyvisa-badge">🎓 Study Visa Guidance</div>
            <h1>Your <span className="va-shine-text va-shine-text-on-dark">Dream University.</span><br />Our Expert Guidance.</h1>
            <p>Over 1,000 students have trusted us to navigate their path to Canada, USA, and Australia. With certified counsellors, a 95%+ visa success rate, and end-to-end support — your study abroad dream starts here.</p>
            <div className="studyvisa-hero-flags">
              <span>🇨🇦 Canada</span>
              <span>🇺🇸 USA</span>
              <span>🇦🇺 Australia</span>
              <span>🇬🇧 UK</span>
              <span>🇩🇪 Germany</span>
              <span>🇳🇿 New Zealand</span>
            </div>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: "easeOut" }}
            className="studyvisa-hero-form-wrap"
          >
            {submitted ? (
              <div className="studyvisa-form-success">
                <div className="studyvisa-form-success-icon">✅</div>
                <h3>Thank You!</h3>
                <p>We'll get back to you within 24 hours.</p>
              </div>
            ) : (
              <form className="studyvisa-hero-form" onSubmit={handleSubmit}>
                <h3>Get Free Consultation</h3>
                <input type="text" placeholder="Full Name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
                <input type="tel" placeholder="Mobile Number" required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
                <input type="email" placeholder="Email Address" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                <SearchableFormDropdown
                  value={form.destination}
                  onChange={(val) => setForm({ ...form, destination: val })}
                  required
                />
                <select required value={form.course} onChange={e => setForm({...form, course: e.target.value})}>
                  <option value="">Select Course Level</option>
                  <option value="Diploma">Diploma</option>
                  <option value="Advanced Diploma">Advanced Diploma</option>
                  <option value="Bachelor's Degree">Bachelor's Degree</option>
                  <option value="Postgraduate Diploma">Postgraduate Diploma</option>
                  <option value="Master's Degree">Master's Degree</option>
                  <option value="MBA">MBA</option>
                  <option value="PhD">PhD</option>
                  <option value="Research Program">Research Program</option>
                  <option value="Certificate Course">Certificate Course</option>
                  <option value="Language Course">Language Course</option>
                  <option value="Not Sure">Not Sure Yet</option>
                </select>
                <MagneticButton as="span" className="w-100">
                  <button type="submit" className="studyvisa-form-submit va-shimmer-btn">Submit Enquiry</button>
                </MagneticButton>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* COUNTRY TABS */}
      <section className="studyvisa-section">
        <div className="container">
          <Reveal as="h2"><FaGraduationCap className="studyvisa-section-icon" /> Country-Wise Breakdown</Reveal>
          
          {/* Mobile Custom Dropdown Menu */}
          <div className="studyvisa-mobile-select-wrap">
            <div ref={dropdownRef} className="custom-dropdown position-relative">
              <button 
                id="studyvisa-country-dropdown-btn"
                type="button"
                className="custom-dropdown-toggle d-flex align-items-center justify-content-between w-100"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <span className="d-flex align-items-center gap-2">
                  <span className="country-flag">{country.flag}</span>
                  <span className="country-name">{country.name}</span>
                </span>
                <FaChevronDown className={`dropdown-chevron ${isDropdownOpen ? "open" : ""}`} />
              </button>
              
              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="custom-dropdown-menu position-absolute w-100 mt-2 shadow-lg"
                  >
                    <div className="dropdown-search-box p-2 d-flex align-items-center gap-2 border-bottom">
                      <FaSearch className="dropdown-search-icon text-muted ms-2" />
                      <input
                        type="text"
                        className="dropdown-search-input w-100 border-0 bg-transparent py-1 ps-1 pe-2 shadow-none"
                        placeholder="Search country..."
                        value={dropdownSearchQuery}
                        onChange={(e) => setDropdownSearchQuery(e.target.value)}
                        autoFocus
                      />
                      {dropdownSearchQuery && (
                        <button
                          type="button"
                          onClick={() => setDropdownSearchQuery("")}
                          className="dropdown-search-clear border-0 bg-transparent text-muted"
                          style={{ fontSize: "1.1rem", padding: "0 0.5rem" }}
                        >
                          &times;
                        </button>
                      )}
                    </div>
                    <div className="dropdown-items-list" style={{ maxHeight: "250px", overflowY: "auto" }}>
                      {filteredCountries.map((c) => (
                        <button
                          id={`studyvisa-country-dropdown-item-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                          key={c.originalIndex}
                          type="button"
                          className={`custom-dropdown-item d-flex align-items-center gap-2 w-100 ${activeCountry === c.originalIndex ? "active" : ""}`}
                          onClick={() => {
                            setActiveCountry(c.originalIndex);
                            setIsDropdownOpen(false);
                            setDropdownSearchQuery("");
                          }}
                        >
                          <span className="country-flag">{c.flag}</span>
                          <span className="country-name">{c.name}</span>
                          {activeCountry === c.originalIndex && <span className="ms-auto selected-dot"></span>}
                        </button>
                      ))}
                      {filteredCountries.length === 0 && (
                        <div className="dropdown-no-results p-3 text-center text-muted">
                          No countries starting with "{dropdownSearchQuery}"
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Desktop Country Tabs */}
          <div className="studyvisa-tabs">
            {countries.map((c, i) => (
              <button 
                id={`studyvisa-tab-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                key={i} 
                className={`studyvisa-tab ${activeCountry === i ? "active" : ""}`} 
                onClick={() => setActiveCountry(i)}
              >
                <span className="country-flag">{c.flag}</span> {c.name}
              </button>
            ))}
          </div>
          <div className="studyvisa-country-panel">
            <div className="row g-4">
              <div className="col-lg-6">
                <motion.div 
                  key={`${country.name}-facts`}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="studyvisa-info-card"
                >
                  <h3><span className="country-flag">{country.flag}</span> {country.name} — Key Facts</h3>
                  <div className="studyvisa-facts">
                    <div className="studyvisa-fact"><span className="fact-label">💰 Tuition</span><span>{country.tuition}</span></div>
                    <div className="studyvisa-fact"><span className="fact-label">💼 Work During Study</span><span>{country.workDuring}</span></div>
                    <div className="studyvisa-fact"><span className="fact-label">✈️ Post-Study Work</span><span>{country.postStudy}</span></div>
                    <div className="studyvisa-fact"><span className="fact-label">🏠 PR Pathway</span><span>{country.pr}</span></div>
                    <div className="studyvisa-fact"><span className="fact-label">📝 IELTS Required</span><span>{country.ielts}</span></div>
                  </div>
                  <h4 className="mt-4 mb-3">🔥 Best Programs</h4>
                  <div className="studyvisa-tags">
                    {country.best.map((b, i) => <span key={i} className="studyvisa-tag">{b}</span>)}
                  </div>
                </motion.div>
              </div>
              <div className="col-lg-6">
                <motion.div 
                  key={`${country.name}-highlights`}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                  className="studyvisa-info-card"
                >
                  <h3>✅ Why {country.name}?</h3>
                  <ul className="studyvisa-list">
                    {country.highlights.map((h, i) => <li key={i}><FaCheckCircle className="studyvisa-check" />{h}</li>)}
                  </ul>
                  <h4 className="mt-4 mb-3">📋 Documents Required</h4>
                  <ul className="studyvisa-list">
                    {country.docs.map((d, i) => <li key={i}><FaCheckCircle className="studyvisa-check" />{d}</li>)}
                  </ul>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OUR SERVICES */}
      <section className="studyvisa-section studyvisa-light">
        <div className="container">
          <Reveal as="h2"><FaHandshake className="studyvisa-section-icon" /> Our Services</Reveal>
          <div className="studyvisa-services-grid">
            {services.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="studyvisa-service-card va-hover-lift"
              >
                <div className="studyvisa-service-icon va-float">{s.icon}</div>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="studyvisa-section">
        <div className="container">
          <Reveal as="h2">📈 How It Works</Reveal>
          <div className="studyvisa-steps">
            {steps.map((s, i) => (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, scale: 0.93 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="studyvisa-step va-hover-lift"
              >
                <div className="studyvisa-step-num">{s.num}</div>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="studyvisa-section studyvisa-light">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>🔍 Quick Comparison</h2>
            <div className="studyvisa-table-wrap">
              <table className="studyvisa-table">
                <thead>
                  <tr>
                    <th>Factor</th>
                    <th>🇨🇦 Canada</th>
                    <th>🇺🇸 USA</th>
                    <th>🇦🇺 Australia</th>
                    <th>🇬🇧 UK</th>
                    <th>🇩🇪 Germany</th>
                    <th>🇳🇿 NZ</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Tuition Cost</td><td>Medium</td><td>High</td><td>High</td><td>High</td><td>Free (Public)</td><td>Medium</td></tr>
                  <tr><td>Post-Study Work</td><td>Up to 3 yrs</td><td>1–3 yrs (OPT)</td><td>2–4 yrs</td><td>2 yrs</td><td>18 months</td><td>Up to 3 yrs</td></tr>
                  <tr><td>PR Speed</td><td>Fast</td><td>Slow</td><td>Medium</td><td>Slow</td><td>Fast</td><td>Medium</td></tr>
                  <tr><td>Work During Study</td><td>24 hrs/week</td><td>20 hrs (on-campus)</td><td>48 hrs/fortnight</td><td>20 hrs/week</td><td>140 days/yr</td><td>20 hrs/week</td></tr>
                  <tr><td>IELTS Requirement</td><td>6.5</td><td>6.5–7.5</td><td>6.0–6.5</td><td>6.0–6.5</td><td>6.0–6.5</td><td>6.0–6.5</td></tr>
                  <tr><td>Best For</td><td>PR seekers</td><td>Top rankings</td><td>Long work visa</td><td>1-yr Master's</td><td>Free education</td><td>Work-life balance</td></tr>
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="studyvisa-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>❓ Frequently Asked Questions</h2>
            <div className="studyvisa-faq">
              {faqs.map((f, i) => (
                <div key={i} className="studyvisa-faq-item va-hover-lift" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <div className="studyvisa-faq-q">
                    <span>{f.q}</span>
                    <FaChevronDown className={`studyvisa-faq-icon ${openFaq === i ? "open" : ""}`} />
                  </div>
                  {openFaq === i && <div className="studyvisa-faq-a">{f.a}</div>}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="studyvisa-cta-section">
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2>Ready to Start Your Study Abroad Journey?</h2>
            <p>Join 1000+ students who achieved their dreams with our expert guidance.</p>
            <MagneticButton as="span">
              <button className="lmbtn" style={{ border: 'none' }}>
                <Link to="/consultationForm" className="cButton">Book Your Free Consultation</Link>
              </button>
            </MagneticButton>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
